import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { RootState } from './store'
import { HostSettings } from 'src/app/types'
import { appApi } from './appApiQueries'

const initialState: HostSettings = {
  showBadges: true,
  showFTE: true,
  activeSession: null,
  groupIds: {},
  groupNames: {},
}

const hostSettingsSlice = createSlice({
  name: 'hostSettings',
  initialState,
  reducers: {
    setShowBadges: (state, { payload }: PayloadAction<boolean>) => {
      state.showBadges = payload
    },
    setShowFTE: (state, { payload }: PayloadAction<boolean>) => {
      state.showFTE = payload
    },
  },
  // allows values to be initial state for the host settings reducer
  extraReducers: (builder) => {
    builder.addMatcher(
      appApi.endpoints.getHostSettings.matchFulfilled,
      (
        state,
        { payload: { showBadges, showFTE, activeSession, groupNames, groupIds } }
      ) => {
        state.showBadges = showBadges
        state.showFTE = showFTE
        state.activeSession = activeSession
        state.groupIds = groupIds
        state.groupNames = groupNames
      }
    )
    // if we created a new session, it will always become the active session
    builder.addMatcher(
      appApi.endpoints.createSession.matchFulfilled,
      (state, { payload }) => {
        state.activeSession = payload
      }
    )
    // if we updated a session and it was the active session, will be returned otherwise null
    builder.addMatcher(
      appApi.endpoints.updateSession.matchFulfilled,
      (state, { payload }) => {
        if (payload) state.activeSession = payload
      }
    )
    // if we deleted a session and it was the active session, clear that from the host settings
    builder.addMatcher(
      appApi.endpoints.deleteSession.matchFulfilled,
      (state, { payload: { wasActiveSession } }) => {
        if (wasActiveSession) state.activeSession = null
      }
    )
    // if we ended our active session, clear it here
    builder.addMatcher(
      appApi.endpoints.endActiveSession.matchFulfilled,
      (state) => {
        state.activeSession = null
      }
    )
    // if we created a new group
    builder.addMatcher(
      appApi.endpoints.createGroup.matchFulfilled,
      (state, { payload: { groupIds, groupNames } }) => {
        state.groupIds = groupIds
        state.groupNames = groupNames
      }
    )
    // if we updated a group
    builder.addMatcher(
      appApi.endpoints.updateGroup.matchFulfilled,
      (state, { payload: { groupIds, groupNames } }) => {
        state.groupIds = groupIds
        state.groupNames = groupNames
      }
    )
  },
})

export const { setShowBadges, setShowFTE } = hostSettingsSlice.actions

export const selectShowBadges = (state: RootState) =>
  state.hostSettings.showBadges
export const selectShowFTE = (state: RootState) =>
    state.hostSettings.showFTE
export const selectStoredActiveSession = (state: RootState) =>
  state.hostSettings.activeSession
export const selectHostSettings = (state: RootState) => state.hostSettings
export const selectGroupIds = (state: RootState) => state.hostSettings.groupIds
export const selectGroupNames = (state: RootState) =>
  state.hostSettings.groupNames
export const selectGroupName = createSelector(
  selectGroupIds,
  (_state: RootState, groupId: string) => groupId,
  (groupIds, groupId) => groupIds[groupId]
)

export default hostSettingsSlice
